import React from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { CheckCircle2, ArrowRight } from "lucide-react";

/**
 * SetupChecklistModal — out-of-the-way setup-checklist surface.
 *
 * The Overview dashboard used to be dominated by a 6-step setup card; that's
 * now decluttered behind a "View setup checklist" button. The checklist
 * itself is still real-state-driven (booleans from the same data the old
 * inline list used).
 */
export default function SetupChecklistModal({ open, onOpenChange, steps, link }) {
  const total = steps.length;
  const done = steps.filter((s) => s.done).length;
  const pct = Math.round((done / Math.max(total, 1)) * 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="setup-checklist-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Setup checklist</DialogTitle>
          <DialogDescription>
            Knock these out in order — most customers are live in under 15 minutes.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold tabular-nums" data-testid="setup-progress-text">{done} / {total} complete · {pct}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }}/>
          </div>
        </div>
        <ol className="space-y-2 max-h-[60vh] overflow-y-auto">
          {steps.map((s, i) => (
            <li
              key={s.id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition ${s.done ? "bg-emerald-50/60 border-emerald-200" : "bg-card hover:bg-muted/40"}`}
              data-testid={`checklist-item-${s.id}`}
            >
              <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-medium shrink-0 ${s.done ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"}`}>
                {s.done ? <CheckCircle2 className="w-4 h-4"/> : i + 1}
              </div>
              <div className="flex-1 text-sm font-medium">{s.label}</div>
              {s.external ? (
                <a href={s.to} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="ghost" data-testid={`checklist-action-${s.id}`}>
                    {s.done ? "Re-test" : "Open"} <ArrowRight className="w-3.5 h-3.5 ml-1"/>
                  </Button>
                </a>
              ) : (
                <Link to={s.to} onClick={() => onOpenChange(false)}>
                  <Button size="sm" variant="ghost" data-testid={`checklist-action-${s.id}`}>
                    {s.done ? "Review" : "Start"} <ArrowRight className="w-3.5 h-3.5 ml-1"/>
                  </Button>
                </Link>
              )}
            </li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
}
